import { apiRoutes } from '../apiRoutes.js';
import {
  clearCodelyOrgId,
  freshLoginUrl,
  loginRedirectUrl,
  renderMissingOrgContextMessage,
} from '../orgContext.js';

const ACCESS_TOKEN_KEY = 'token';
const TOKEN_REFRESH_LEEWAY_MS = 15_000;
const MIN_TOKEN_REFRESH_DELAY_MS = 5_000;

let refreshPromise = null;
let refreshTimer = null;
let accessTokenConsumer = null;
let sessionExpired = false;

export function bindAccessTokenConsumer(consumer) {
  accessTokenConsumer = consumer;
  const token = getAccessToken();
  if (token) {
    applyAccessToken(token);
    scheduleTokenRefresh(token);
  }
}

export function getAccessToken() {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY) || '';
}

export function setAccessToken(token) {
  if (!token) {
    clearAccessToken();
    return;
  }
  sessionExpired = false;
  sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  applyAccessToken(token);
  scheduleTokenRefresh(token);
}

export function clearAccessToken() {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  applyAccessToken('');
  clearRefreshTimer();
}

export async function refreshSession() {
  if (sessionExpired) {
    return false;
  }
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    try {
      const response = await fetch(apiRoutes.refreshToken(), {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        await expireSession();
        return false;
      }
      const content = await response.json();
      if (!content?.token) {
        await expireSession();
        return false;
      }
      setAccessToken(content.token);
      return true;
    } catch (err) {
      console.debug('[Auth] Refresh session request failed.', err);
      if (String(err?.message || '').includes('organization context')) {
        renderMissingOrgContextMessage();
      }
      await expireSession();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function ensureFreshSession(leewayMs = TOKEN_REFRESH_LEEWAY_MS) {
  const token = getAccessToken();
  if (!token) {
    return refreshSession();
  }
  const expiryMs = readJwtExpiryMs(token);
  if (!expiryMs || expiryMs - Date.now() > leewayMs) {
    return true;
  }
  return refreshSession();
}

export async function authenticatedFetch(input, init = {}) {
  const sessionReady = await ensureFreshSession();
  if (!sessionReady) {
    return new Response(null, { status: 401, statusText: 'Session expired' });
  }

  const firstResponse = await fetchWithAccessToken(input, init);
  if (firstResponse.status !== 401) {
    return firstResponse;
  }

  // Token was rejected mid-flight (clock skew, server-side early expiry, etc).
  // Try one silent refresh-and-retry before giving up.
  const refreshed = await refreshSession();
  if (!refreshed) {
    return firstResponse;
  }
  return fetchWithAccessToken(input, init);
}

export async function logoutSession() {
  try {
    await fetch(apiRoutes.logout(), {
      method: 'GET',
      headers: authorizationHeaders(),
      credentials: 'include',
    });
  } finally {
    await expireSession(false);
    clearCodelyOrgId();
  }
}

export function redirectToPortalLogin() {
  window.location.href = codelyLoginUrl();
}

export function redirectToFreshPortalLogin() {
  window.location.href = freshLoginUrl();
}

async function expireSession(redirect = true) {
  if (sessionExpired) {
    return;
  }
  sessionExpired = true;
  clearAccessToken();
  if (redirect && !isLoginPage()) {
    redirectToPortalLogin();
  }
}

function fetchWithAccessToken(input, init) {
  const headers = new Headers(init.headers || {});
  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(input, {
    ...init,
    headers,
    credentials: 'include',
  });
}

function authorizationHeaders() {
  const headers = new Headers();
  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

function applyAccessToken(token) {
  if (accessTokenConsumer) {
    accessTokenConsumer(token);
  }
}

function scheduleTokenRefresh(token) {
  clearRefreshTimer();
  const expiryMs = readJwtExpiryMs(token);
  if (!expiryMs) {
    return;
  }
  const delayMs = Math.max(
    expiryMs - Date.now() - TOKEN_REFRESH_LEEWAY_MS,
    MIN_TOKEN_REFRESH_DELAY_MS
  );
  refreshTimer = window.setTimeout(() => {
    refreshTimer = null;
    if (getAccessToken() !== token) {
      return;
    }
    void refreshSession();
  }, delayMs);
}

function clearRefreshTimer() {
  if (!refreshTimer) {
    return;
  }
  window.clearTimeout(refreshTimer);
  refreshTimer = null;
}

function readJwtExpiryMs(jwt) {
  const [, payload] = jwt.split('.');
  if (!payload) {
    return null;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const decoded = JSON.parse(window.atob(padded));
    const exp = Number(decoded?.exp);
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function isLoginPage() {
  return (
    window.location.pathname.includes('login_redirect') ||
    window.location.pathname.includes('codelylogin')
  );
}

function codelyLoginUrl() {
  return loginRedirectUrl();
}
